// #336 slice 5, brief §5.4b point 2: the presets of the «Regola» sheet and
// their fields, each with its step and its bounds. The rules they make live in
// programProgressionPresets. Keep free of React and any UI import.
import type { EffortScale } from "./effortScale.js";
import { DEFAULT_LOAD_STEP, DEFAULT_START_LOAD, loadStepFor } from "./programProgression.js";
import type { UnitsPreference } from "./schemas/profile.js";

export type PresetId =
  | "constant" | "effortRamp" | "linearKg" | "repsThenLoad" | "timed" | "history" | "waves" | "triple"
  | "pyramid" | "repsUp" | "topBackoff";

/** The order of the sheet's row: the six of the mock, then Onde, the triple
 *  progression, the pyramid, rising reps, top and back-off (Antenore, 12/10). */
export const PRESET_IDS: readonly PresetId[] = [
  "constant", "effortRamp", "linearKg", "repsThenLoad", "timed", "history", "waves", "triple",
  "pyramid", "repsUp", "topBackoff",
];

/** What a field counts, which sets its step (FIELD_STEP). */
export type FieldUnit = "sets" | "reps" | "effort" | "load" | "percent" | "seconds";
/** A field of a preset: what it counts, its bounds in the cells' unit,
 *  `delta` when it is a step (spelled with its sign, «+2.5 kg», «−2»), and
 *  `optional` when 0 means «none» (a cap). An effort's bounds are on RPE:
 *  fieldBounds moves them to RIR. A start load's floor is one step. */
export type PresetField = { key: string; unit: FieldUnit; min: number; max: number; delta?: true; optional?: true };

/** One step of each unit. The load's is in kg: loadStepFor(unit) in lb. */
export const FIELD_STEP: Record<FieldUnit, number> = {
  sets: 1, reps: 1, effort: 0.5, load: DEFAULT_LOAD_STEP, percent: 5, seconds: 5,
};

const f = (key: string, unit: FieldUnit, min: number, max: number, kind?: "delta" | "optional"): PresetField => ({
  key, unit, min, max,
  ...(kind === "delta" ? { delta: true as const } : kind === "optional" ? { optional: true as const } : {}),
});

const SETS = f("sets", "sets", 1, 10);
const REPS = f("reps", "reps", 1, 50);
const EFFORT = f("effort", "effort", 1, 10);
const REPS_MIN = f("repsMin", "reps", 1, 50);
const REPS_MAX = f("repsMax", "reps", 1, 50);
const REPS_STEP = f("repsStep", "reps", 1, 10);
const LOAD = f("load", "load", 0, 1000);
const LOAD_STEP = f("loadStep", "load", 0, 50, "delta");

/** §5.4b point 2, field by field. */
export const PRESET_FIELDS: Record<PresetId, readonly PresetField[]> = {
  constant: [SETS, REPS, EFFORT],
  effortRamp: [SETS, REPS, EFFORT, f("step", "effort", 0, 3, "delta"), f("cap", "effort", 1, 10)],
  linearKg: [SETS, REPS, LOAD, f("step", "load", 0, 50, "delta"), f("deloadPct", "percent", 50, 100)],
  repsThenLoad: [SETS, REPS_MIN, REPS_MAX, REPS_STEP, LOAD, LOAD_STEP],
  timed: [SETS, f("seconds", "seconds", 5, 600), f("step", "seconds", 0, 60, "delta")],
  history: [SETS, REPS],
  waves: [SETS, REPS, f("efforts", "effort", 1, 10)],
  triple: [SETS, f("setsMax", "sets", 1, 10), REPS_MIN, REPS_MAX, REPS_STEP, LOAD, LOAD_STEP],
  // The steps inside the round take either sign: an inverted pyramid is the same preset.
  pyramid: [
    SETS, REPS, f("repsSetStep", "reps", -10, 10, "delta"), LOAD, f("loadSetStep", "load", -50, 50, "delta"),
    f("step", "load", 0, 50, "delta"), f("deloadPct", "percent", 50, 100),
  ],
  repsUp: [SETS, REPS, f("step", "reps", 0, 10, "delta"), f("cap", "reps", 0, 100, "optional")],
  topBackoff: [
    f("backoffSets", "sets", 1, 9), REPS, f("topEffort", "effort", 1, 10), f("backoffEffort", "effort", 1, 10),
    f("step", "effort", 0, 3, "delta"),
  ],
};

/** A field's step in the cells' `unit`. */
export const fieldStep = (field: PresetField, unit: UnitsPreference): number =>
  field.unit === "load" ? loadStepFor(unit) : FIELD_STEP[field.unit];

/** A field's value; `efforts` (Onde) is the cycle. */
export type PresetValues = Record<string, number | number[]>;

/** A field's [min, max]: an effort on RIR reaches 0, a start load never goes
 *  under one step. On the pyramid (`preset`) the first set, the step between
 *  sets and the set count bound each other: no set under 1 rep, no load under
 *  one step. */
export const fieldBounds = (
  field: PresetField,
  scale: EffortScale,
  unit: UnitsPreference,
  preset?: { id: PresetId; values: PresetValues },
): [number, number] => {
  let lo = field.min;
  let hi = field.max;
  if (field.unit === "effort" && !field.delta && scale === "rir") lo = 0;
  if (field.unit === "load" && !field.delta) lo = Math.max(field.min, loadStepFor(unit));
  const tie = preset?.id === "pyramid" ? pyramidBounds(field.key, preset.values, unit) : null;
  if (tie) {
    lo = Math.max(lo, tie[0]);
    hi = Math.min(hi, tie[1]);
  }
  return [lo, Math.max(lo, hi)];
};

/** The pyramid's ties: the last set is reps + repsSetStep × (sets − 1) reps
 *  and load + loadSetStep × (sets − 1), never under 1 rep and one load step. */
const pyramidBounds = (key: string, v: PresetValues, unit: UnitsPreference): [number, number] | null => {
  const q = loadStepFor(unit);
  const sets = Math.max(1, fieldNum(v, "sets", 1));
  const gaps = sets - 1;
  const reps = fieldNum(v, "reps", 12);
  const rs = fieldNum(v, "repsSetStep", 0);
  const load = fieldNum(v, "load", DEFAULT_START_LOAD);
  const ls = fieldNum(v, "loadSetStep", 0);
  const eps = 1e-9;
  switch (key) {
    case "repsSetStep":
      return gaps > 0 ? [-Math.floor((reps - 1) / gaps + eps), Infinity] : null;
    case "reps":
      return rs < 0 ? [1 - rs * gaps, Infinity] : null;
    case "loadSetStep":
      return gaps > 0 ? [-Math.floor((load - q) / (gaps * q) + eps) * q, Infinity] : null;
    case "load":
      return ls < 0 ? [q - ls * gaps, Infinity] : null;
    case "sets": {
      const byReps = rs < 0 ? 1 + Math.floor((reps - 1) / -rs + eps) : Infinity;
      const byLoad = ls < 0 ? 1 + Math.floor((load - q) / -ls + eps) : Infinity;
      return [1, Math.min(byReps, byLoad)];
    }
    default:
      return null;
  }
};

/** A pyramid's steps between sets raised until its last set has 1 rep and
 *  one load step: a row of many sets opened as a pyramid. */
export const fitPyramid = (v: PresetValues, unit: UnitsPreference): PresetValues => {
  const out = { ...v };
  for (const key of ["repsSetStep", "loadSetStep"]) {
    const b = pyramidBounds(key, out, unit);
    if (b && fieldNum(out, key, 0) < b[0]) out[key] = b[0];
  }
  return out;
};

export const fieldNum = (v: PresetValues, k: string, dflt: number): number => {
  const x = v[k];
  return typeof x === "number" ? x : dflt;
};
